import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import { api } from '../lib/api';
import { playbackBus } from '../lib/playbackBus';

const AudioPlayerContext = createContext(null);

export const useAudioPlayer = () => {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error('useAudioPlayer must be used within an AudioPlayerProvider');
  }
  return context;
};

export const AudioPlayerProvider = ({ children }) => {
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [totalListenTime, setTotalListenTime] = useState(0);
  const [currentSource, setCurrentSource] = useState('queue');
  const audioRef = useRef(null);
  const listenStartRef = useRef(null);
  const playStartTimeRef = useRef(null);
  const hasTrackedClickRef = useRef(false);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  // Track a play event (click, full_play, etc)
  const trackPlayEvent = useCallback(async (songId, eventType, source, token, durationSeconds = null) => {
    if (!token || !songId) return;
    
    try {
      await api.trackSongPlay(token, songId, eventType, source, durationSeconds);
      console.log(`Tracked ${eventType} for song ${songId} from ${source}`);
    } catch (err) {
      console.error('Failed to track play event:', err);
    }
  }, []);

  const startSession = useCallback(async (submissionId, token) => {
    try {
      const res = await fetch(`${API}/play-sessions/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ submission_id: submissionId })
      });
      if (res.ok) {
        const data = await res.json();
        setSessionId(data.id);
        listenStartRef.current = Date.now();
        playStartTimeRef.current = Date.now();
        return data;
      }
    } catch (err) {
      console.error('Failed to start session:', err);
    }
    return null;
  }, [API]);

  const endSession = useCallback(async (token) => {
    if (!sessionId) return;
    
    const listenedSeconds = Math.floor((Date.now() - (listenStartRef.current || Date.now())) / 1000);
    const newTotal = totalListenTime + listenedSeconds;
    
    try {
      await fetch(`${API}/play-sessions/end`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          session_id: sessionId,
          total_listen_seconds: newTotal
        })
      });
      setTotalListenTime(newTotal);
    } catch (err) {
      console.error('Failed to end session:', err);
    }
    
    setSessionId(null);
    listenStartRef.current = null;
  }, [sessionId, totalListenTime, API]);

  const playTrack = useCallback(async (track, submissionId, token, source = 'queue') => {
    // If already playing same track, just resume
    if (currentTrack?.id === track.id && audioRef.current) {
      playbackBus.claim(track.id);
      audioRef.current.play();
      setIsPlaying(true);
      if (!sessionId && submissionId) {
        await startSession(submissionId, token);
      }
      return;
    }

    // End previous session if exists
    if (sessionId && token) {
      await endSession(token);
    }

    // Take global single-song lock — auto-pauses every other player.
    playbackBus.claim(track.id);

    // Track click event for new track
    if (track.id && token && !hasTrackedClickRef.current) {
      await trackPlayEvent(track.id, 'click', source, token);
      hasTrackedClickRef.current = true;
    }

    setCurrentTrack(track);
    setCurrentSource(source);
    setTotalListenTime(0);
    setCurrentTime(0);
    playStartTimeRef.current = Date.now();
    
    // Start new session
    if (submissionId && token) {
      await startSession(submissionId, token);
    }

    // Play will be triggered by audio element's canplay event
    setIsPlaying(true);
  }, [currentTrack, sessionId, startSession, endSession, trackPlayEvent]);

  const pauseTrack = useCallback(async (token) => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      playbackBus.release(currentTrack?.id);
      
      if (sessionId && token) {
        await endSession(token);
      }
    }
  }, [sessionId, endSession, currentTrack]);

  const seekTo = useCallback((time) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  }, []);

  // Called when track ends naturally (full play)
  const onTrackEnded = useCallback(async (token) => {
    if (currentTrack?.id && token && playStartTimeRef.current) {
      const playDuration = Math.floor((Date.now() - playStartTimeRef.current) / 1000);
      await trackPlayEvent(currentTrack.id, 'full_play', currentSource, token, playDuration);
    }
    hasTrackedClickRef.current = false;
  }, [currentTrack, currentSource, trackPlayEvent]);

  const stopTrack = useCallback(async (token) => {
    if (sessionId && token) {
      await endSession(token);
    }
    hasTrackedClickRef.current = false;
    setCurrentTrack(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setTotalListenTime(0);
  }, [sessionId, endSession]);

  const isEligibleForDecision = totalListenTime >= 30;

  const value = {
    currentTrack,
    isPlaying,
    currentTime,
    duration,
    totalListenTime,
    isEligibleForDecision,
    audioRef,
    playTrack,
    pauseTrack,
    seekTo,
    stopTrack,
    onTrackEnded,
    trackPlayEvent,
    setCurrentTime,
    setDuration,
    setIsPlaying,
    currentSource
  };

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
    </AudioPlayerContext.Provider>
  );
};
